import React, { useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { AlertTriangle, Clock, MapPin } from 'lucide-react';
import { FallIncident, RiskZone, TimeBasedRisk } from '../types';

interface RiskAssessmentTabProps {
  riskZones: RiskZone[];
  timeBasedRisks: TimeBasedRisk[];
  incidents: FallIncident[];
}

const RiskAssessmentTab: React.FC<RiskAssessmentTabProps> = ({
  riskZones,
  timeBasedRisks,
  incidents
}) => {
  const [selectedZone, setSelectedZone] = useState<RiskZone | null>(null);

  // Process data for visualizations
  const riskLevelData = riskZones.reduce((acc, zone) => {
    const level = zone.riskLevel;
    acc[level] = (acc[level] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const riskLevelChartData = Object.entries(riskLevelData).map(([name, value]) => ({
    name,
    value
  }));

  const timeData = timeBasedRisks.map(risk => ({
    time: risk.timeOfDay,
    risk: risk.riskLevel,
    incidents: incidents.filter(i => {
      const hour = new Date(i.timestamp).getHours();
      return hour === parseInt(risk.timeOfDay.split(':')[0]);
    }).length
  }));

  const COLORS = {
    HIGH: '#EF4444',
    MEDIUM: '#F59E0B',
    LOW: '#10B981'
  };

  return (
    <div className="space-y-6">
      {/* Top Row: Risk Level Distribution and Risk Factors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Risk Level Distribution */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Risk Level Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={riskLevelChartData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {riskLevelChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[entry.name as keyof typeof COLORS]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Risk Factors */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Risk Factors</h3>
          <div className="space-y-4">
            {riskZones
              .filter(zone => zone.riskLevel === 'HIGH')
              .slice(0, 5)
              .map(zone => (
                <div
                  key={zone.id}
                  className="p-4 bg-red-50 rounded-lg border border-red-200 cursor-pointer hover:bg-red-100"
                  onClick={() => setSelectedZone(zone)}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium text-red-900">
                        {zone.location.room}
                      </h4>
                      <p className="text-sm text-red-700 mt-1">
                        {zone.riskFactors.join(', ')}
                      </p>
                    </div>
                    <AlertTriangle className="text-red-500" size={20} />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Time-based Risk Analysis (Full Width) */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold mb-4">Time-based Risk Analysis</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="risk"
                stroke="#8B5CF6"
                name="Risk Level"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="incidents"
                stroke="#3B82F6"
                name="Incidents"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Risk Zone Details */}
      {selectedZone && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h3 className="text-lg font-semibold">
                Risk Zone: {selectedZone.location.room}
              </h3>
              <p className="text-gray-600 mt-1">
                Detailed analysis and recommendations
              </p>
            </div>
            <button
              onClick={() => setSelectedZone(null)}
              className="text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Risk Factors</h4>
              <ul className="space-y-2">
                {selectedZone.riskFactors.map((factor, index) => (
                  <li key={index} className="flex items-center text-gray-700">
                    <AlertTriangle className="text-red-500 mr-2" size={16} />
                    {factor}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-medium text-gray-900 mb-2">
                Recommended Actions
              </h4>
              <ul className="space-y-2">
                {selectedZone.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start text-gray-700">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mr-2">
                      {index + 1}
                    </span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <h4 className="font-medium text-gray-900 mb-2">Incident History</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Severity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {incidents
                      .filter(
                        i =>
                          i.location.room === selectedZone.location.room &&
                          i.location.zone === selectedZone.location.zone
                      )
                      .map(incident => (
                        <tr key={incident.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(incident.timestamp).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                incident.severity === 'HIGH'
                                  ? 'bg-red-100 text-red-800'
                                  : incident.severity === 'MEDIUM'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-green-100 text-green-800'
                              }`}
                            >
                              {incident.severity}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {incident.description}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskAssessmentTab; 